import { addDays } from 'date-fns'
import { PaginationPayload, SortPayload } from 'react-admin'
import CFG from 'services/cfg'
import {
  DATE_RANGE_END_FILTER_SOURCES,
  FilterSource,
  NON_STRING_FILTER_SOURCES,
} from 'services/cfg/filter'
import { entries } from '.'
import { removeMillisecondsFromIso } from './date'

export type QueryFilterObject = Partial<
  Record<FilterSource, string | (string | number)[]>
>

export type QueryObject = {
  pagination?: PaginationPayload
  filter?: QueryFilterObject
  sort?: SortPayload
}

export const encodeSpaces = (v: string) => v.replace(/\s/g, '+')
export const encodePlus = (v: string) => v.replace(/\+/g, '%20')
export const encodeSpacesAndPlus = (v: string) => encodeSpaces(encodePlus(v))

/**
 * @param  q query object containing pagination filtering and sorting data
 * @returns the query string to send with the get all request
 */
export const encodeQueryStringFromQueryObject = (q: QueryObject): string => {
  const { filter, sort, pagination } = q
  const encodedFilters = encodeFilters(filter!)
  const encodedSort = encodeSort(sort)
  const { page, perPage } = encodePaginationParams(pagination)

  const queryString = [
    [page, 'page'],
    [perPage, 'perPage'],
    [encodedFilters, 'filter'],
    [encodedSort, 'sort'],
  ]
    .filter(
      ([encodedValue]) => encodedValue !== undefined && encodedValue !== ''
    )
    .map(([encodedValue, qsKey]) => `${qsKey}=${encodedValue}`)
    .join('&')
  return encodeSpacesAndPlus(queryString)
}

const addOneToRangeEndDate = (date: string, source: FilterSource) =>
  DATE_RANGE_END_FILTER_SOURCES.includes(source)
    ? removeMillisecondsFromIso(addDays(new Date(date), 1).toISOString())
    : date

const turnStringsIntoStrings = (value: string, source: FilterSource) =>
  NON_STRING_FILTER_SOURCES.includes(source) ? value : `'${value}'`

/**
 * @param v: object of filters to map
 * @returns same object with string values mapped from a to 'a'
 */
const processFilterValue = (
  v: string | (string | number)[],
  s: FilterSource
) => {
  const stringifiedValue = Array.isArray(v)
    ? encodeStringArray(v)
    : turnStringsIntoStrings(v, s)
  return addOneToRangeEndDate(stringifiedValue, s)
}

/**
 * @param  q object of query filters
 * @returns the query string for the filters
 */
export const encodeFilters = (q: QueryFilterObject): string => {
  return entries(q as Record<FilterSource, string>)
    .map(([k, v]) => k + processFilterValue(v, k))
    .join('~and~')
}

/**
 * @param field to sort
 * @param order asc or desc
 * @returns the query for sorting if field is not id
 */
export const encodeSort = (sort?: SortPayload) => {
  if (
    sort &&
    (sort.order.toLowerCase() === 'asc' ||
      sort.order.toLowerCase() === 'desc') &&
    sort.field !== 'id'
  )
    return `${sort.field}-${sort.order.toLowerCase()}`
  else return ''
}

/**
 * @param  p pagination params with page and per page
 * @returns the array of pagination params strings
 */
const encodePaginationParams = (
  p?: PaginationPayload
): { page?: number; perPage?: number } =>
  !p || p?.perPage === CFG.perPageDefault ? {} : p

const encodeStringArray = (param: (string | number)[]) => param.join(',')
